import React, { useState, useContext } from 'react';
import { Heart, Book } from 'iconsax-react';
import { ThemeContext } from '../theme';

const HeadphoneIcon = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 18v-6a9 9 0 0118 0v6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const VideoPlayIcon = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8"/>
    <path d="M10 8.5l6 3.5-6 3.5V8.5z" fill={color}/>
  </svg>
);

const SCALE = 0.75;
const s = (n) => Math.round(n * SCALE);

/**
 * StoryPreviewModal — shown when user clicks a story card in the Library.
 *
 * Props:
 *   story      — { image, title, category, description?, ageGroup? }
 *   onClose    — callback to close
 *   isClosing  — boolean to trigger close animation
 */
export default function StoryPreviewModal({ story, onClose, isClosing, onModeSelect }) {
  const theme = useContext(ThemeContext);
  const [liked, setLiked] = useState(false);

  const description = story.description ||
    'A gentle, heartfelt story to share with your little one. Perfect for bedtime or a quiet afternoon together.';
  const ageGroup = story.ageGroup || '3 to 6 year old';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 210,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: isClosing
          ? 'backdropFadeOut 0.3s ease forwards'
          : 'backdropFadeIn 0.18s ease forwards',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: theme.cardBg,
          borderRadius: s(30),
          width: s(720),
          padding: s(24),
          display: 'flex',
          flexDirection: 'column',
          gap: s(40),
          animation: isClosing
            ? 'modalFadeOut 0.3s ease forwards'
            : 'modalSpringIn 0.4s ease forwards',
        }}
      >
        {/* Cover image with overlaid title + actions */}
        <div style={{ position: 'relative', borderRadius: s(20), overflow: 'hidden', height: s(320), flexShrink: 0 }}>
          <img
            src={story.image}
            alt={story.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(78,65,54,0) 40%, rgba(2,2,2,0.55) 100%)',
            borderRadius: s(20),
          }} />

          {/* Close button — top left */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: s(16),
              left: s(16),
              width: s(48),
              height: s(48),
              borderRadius: s(24),
              backgroundColor: 'rgba(16,16,16,0.5)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width={s(20)} height={s(20)} viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Heart button — top right */}
          <button
            onClick={() => setLiked(l => !l)}
            style={{
              position: 'absolute',
              top: s(16),
              right: s(16),
              width: s(48),
              height: s(48),
              borderRadius: s(24),
              backgroundColor: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(2px)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Heart size={s(24)} variant={liked ? 'Bold' : 'Linear'} color={liked ? '#d46c6c' : '#FFFFFF'} />
          </button>

          {/* Title + category on image */}
          <div style={{
            position: 'absolute',
            bottom: s(28),
            left: s(32),
            right: s(80),
            display: 'flex',
            flexDirection: 'column',
            gap: s(4),
          }}>
            <span
              className="font-satoshi"
              style={{ fontSize: s(32), lineHeight: `${s(40)}px`, fontWeight: 900, color: '#FFFFFF', letterSpacing: '0.32px' }}
            >
              {story.title}
            </span>
            <span
              className="font-satoshi"
              style={{ fontSize: s(16), lineHeight: `${s(24)}px`, fontWeight: 400, color: '#D9D9D9' }}
            >
              {story.category} | Age Group: {ageGroup}
            </span>
          </div>
        </div>

        {/* Description */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: s(16) }}>
          <p
            className="font-satoshi"
            style={{ margin: 0, fontSize: s(16), lineHeight: `${s(24)}px`, fontWeight: 400, color: theme.text200 }}
          >
            {description}
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: s(12) }}>
            {[
            { label: 'Read',   mode: 'read',   renderIcon: (sz) => <Book size={sz} variant="Linear" color={theme.btnPrimaryText} /> },
            { label: 'Listen', mode: 'listen', renderIcon: (sz) => <HeadphoneIcon size={sz} color={theme.btnPrimaryText} /> },
            { label: 'Watch',  mode: 'watch',  renderIcon: (sz) => <VideoPlayIcon size={sz} color={theme.btnPrimaryText} /> },
          ].map(({ label, mode, renderIcon }) => (
            <button
              key={label}
              onClick={() => onModeSelect && onModeSelect(mode)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: s(12),
                backgroundColor: theme.btnPrimaryBg,
                border: 'none',
                borderRadius: s(100),
                padding: s(20),
                cursor: 'pointer',
              }}
            >
              {renderIcon(s(24))}
              <span
                className="font-satoshi font-bold"
                style={{ fontSize: s(18), lineHeight: `${s(24)}px`, color: theme.btnPrimaryText }}
              >
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
